import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type PracticePrompt = {
  id: string;
  title: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard';
  focus: string;
  durationMinutes: number;
  tags?: string[];
};

export type PracticeSessionSummary = {
  id: string;
  role: string;
  status: 'completed' | 'in-progress';
  lastActivity: string;
  score: number;
  focus: string;
};

export type CareerPathStep = {
  id: string;
  label: string;
  status: 'completed' | 'in-progress' | 'up-next';
  description: string;
  timeline: string;
};

export type CareerPathRecommendation = {
  id: string;
  title: string;
  description: string;
  actionLabel: string;
  actionLink: string;
};

export type CareerPathResponse = {
  currentRole: string;
  targetRole: string;
  summary: string;
  steps: CareerPathStep[];
  recommendations: CareerPathRecommendation[];
};

export type PracticeHubResponse = {
  practicePrompts: PracticePrompt[];
  recentSessions: PracticeSessionSummary[];
  aiCoachTips: string[];
  careerPath: CareerPathResponse;
  calculators: {
    commuteDefaults: CommuteCalculatorPayload;
    salaryDefaults: SalaryCalculatorPayload;
  };
};

export type PracticeSessionStartPayload = {
  role: string;
  focusArea?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  occupationId?: string;
};

export type PracticeSessionStartResponse = {
  sessionId: string;
  openingQuestion: string;
  guidance: string[];
};

export type PracticeChatResponse = {
  reply: string;
  followUpQuestion?: string;
  coachingTips?: string[];
  done?: boolean;
};

export type CommuteCalculatorPayload = {
  origin: string;
  destination: string;
  mode?: 'public' | 'drive' | 'cycle' | 'walk';
};

export type CommuteCalculatorResult = {
  durationMinutes: number;
  weeklyCost: number;
  annualCost: number;
  carbonKg: number;
  insights: string[];
};

export type SalaryCalculatorPayload = {
  role: string;
  location: string;
  experienceYears?: number;
};

export type SalaryCalculatorResult = {
  marketRange: {
    min: number;
    median: number;
    max: number;
  };
  percentile: number;
  insights: string[];
};

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required for practice features.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Practice hub request failed (${response.status}): ${errorText}`);
  }
  return (await response.json()) as T;
}

export async function fetchPracticeHub(): Promise<PracticeHubResponse> {
  return apiFetch<PracticeHubResponse>('/v1/candidate/practice-hub');
}

export async function startPracticeSession(
  payload: PracticeSessionStartPayload,
): Promise<PracticeSessionStartResponse> {
  return apiFetch<PracticeSessionStartResponse>('/v1/candidate/practice/sessions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function sendPracticeMessage(
  sessionId: string,
  message: string,
): Promise<PracticeChatResponse> {
  return apiFetch<PracticeChatResponse>(`/v1/candidate/practice/sessions/${sessionId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

export async function calculateCommute(
  payload: CommuteCalculatorPayload,
): Promise<CommuteCalculatorResult> {
  return apiFetch<CommuteCalculatorResult>('/v1/candidate/calculators/commute', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function calculateSalary(
  payload: SalaryCalculatorPayload,
): Promise<SalaryCalculatorResult> {
  return apiFetch<SalaryCalculatorResult>('/v1/candidate/calculators/salary', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function fetchCareerPath(): Promise<CareerPathResponse> {
  return apiFetch<CareerPathResponse>('/v1/candidate/career-path');
}
